// LOP - Product Item Swatch Image Change Functionality
// Changes product image when clicking on color swatches in product cards
// Also handles hover to show second image
(function() {
    'use strict';
    
    // Cache for preloaded images
    const preloadedImages = new Map();
    
    // Use WeakSet to track initialized elements (more reliable with DOM changes)
    const initializedHover = new WeakSet();
    
    // Debounce helper
    function debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }
    
    // ============================================
    // EVENT DELEGATION - Global handlers for swatches and hover
    // ============================================
    function setupGlobalSwatchClickHandler() {
      document.addEventListener('click', function(e) {
        if (!e.target || !e.target.closest) return;
        
        const swatch = e.target.closest('.product-swatches-options__item--swatch');
        if (!swatch) return;
        
        const productItem = swatch.closest('.product-item');
        if (!productItem) return;
        
        const variantImageUrl = swatch.getAttribute('data-variant-image-url');
        if (!variantImageUrl || variantImageUrl.trim() === '') return;
        
        e.preventDefault();
        // Cards inside featured-collection-slider-edit are handled by that section's own JS
        if (productItem.closest('[data-section-type="featured-collection-slider-edit"]')) {
          return;
        }
        e.stopPropagation();
        
        handleSwatchClick(swatch, productItem);
        
      }, true);
    }
    
    function setupGlobalHoverHandler() {
      document.addEventListener('mouseenter', function(e) {
        if (!e.target || !e.target.closest) return;
        
        const imageLink = e.target.closest('.product-item__image-link');
        if (!imageLink) return;
        
        const productItem = imageLink.closest('.product-item');
        if (!productItem) return;
        // Skip hover for gallery cards (section edit with switch ON)
        if (productItem.querySelector('.product-item__media--gallery')) return;
        
        const secondImageWrapper = productItem.querySelector('.product-item__image--two');
        if (!secondImageWrapper) return;
        
        const secondImage = secondImageWrapper.querySelector('img');
        if (secondImage && secondImage.src && secondImage.src !== '' && !secondImage.src.endsWith('undefined')) {
          showSecondImage(secondImageWrapper);
        }
      }, true);
      
      document.addEventListener('mouseleave', function(e) {
        if (!e.target || !e.target.closest) return;
        
        const imageLink = e.target.closest('.product-item__image-link');
        if (!imageLink) return;
        
        const productItem = imageLink.closest('.product-item');
        if (!productItem) return;
        // Skip hover for gallery cards (section edit with switch ON)
        if (productItem.querySelector('.product-item__media--gallery')) return;
        
        const secondImageWrapper = productItem.querySelector('.product-item__image--two');
        if (secondImageWrapper) {
          hideSecondImage(secondImageWrapper);
        }
      }, true);
    }
    
    function handleSwatchClick(swatch, productItem) {
      const variantImageUrl = swatch.getAttribute('data-variant-image-url');
      const variantImageSrcset = swatch.getAttribute('data-variant-image-srcset') || '';
      const variantSecondImageUrl = swatch.getAttribute('data-variant-second-image-url');
      const variantSecondImageSrcset = swatch.getAttribute('data-variant-second-image-srcset') || '';
      const variantPrice = swatch.getAttribute('data-variant-price');
      const variantCompareAtPrice = swatch.getAttribute('data-variant-compare-at-price');
      
      const currentImage = getProductImage(productItem);
      if (!currentImage) return;
      
      // Update price display
      if (variantPrice) {
        updateProductPrice(productItem, variantPrice, variantCompareAtPrice);
      }
      
      // Remove active class from all swatches in this product
      const allSwatches = productItem.querySelectorAll('.product-swatches-options__item--swatch');
      allSwatches.forEach((s) => {
        s.classList.remove('LOP-swatch-active');
      });
      
      // Add active class to clicked swatch
      swatch.classList.add('LOP-swatch-active');
      
      // Update product links with variant ID
      const variantId = swatch.getAttribute('data-variant-id');
      if (variantId) {
        const imageLink = productItem.querySelector('.product-item__image-link');
        const titleLink = productItem.querySelector('.product-item__product-title a');
        
        [imageLink, titleLink].forEach((link) => {
          if (link) {
            const currentHref = link.getAttribute('href');
            if (currentHref) {
              const baseUrl = currentHref.split('?')[0];
              link.setAttribute('href', baseUrl + '?variant=' + variantId);
            }
          }
        });
      }
      
      // Normalize and set main image
      const normalizedUrl = normalizeImageUrl(variantImageUrl);
      let normalizedSrcset = '';
      if (variantImageSrcset) {
        normalizedSrcset = variantImageSrcset.split(', ').map(part => {
          const parts = part.trim().split(/\s+/);
          const url = parts[0];
          const size = parts.slice(1).join(' ');
          return normalizeImageUrl(url) + (size ? ' ' + size : '');
        }).join(', ');
      }
      
      if (normalizedSrcset) {
        currentImage.srcset = normalizedSrcset;
      } else {
        currentImage.removeAttribute('srcset');
      }
      
      currentImage.decoding = 'sync';
      currentImage.loading = 'eager';
      currentImage.src = normalizedUrl;
      
      // Update second/hover image
      let currentSecondImage = productItem.querySelector('.product-item__image--two img');
      
      if (currentSecondImage) {
        let normalizedSecondUrl = '';
        let normalizedSecondSrcset = '';
        
        if (variantSecondImageUrl) {
          normalizedSecondUrl = normalizeImageUrl(variantSecondImageUrl);
          
          if (variantSecondImageSrcset) {
            normalizedSecondSrcset = variantSecondImageSrcset.split(', ').map(part => {
              const parts = part.trim().split(/\s+/);
              const url = parts[0];
              const size = parts.slice(1).join(' ');
              return normalizeImageUrl(url) + (size ? ' ' + size : '');
            }).join(', ');
          }
        } else {
          normalizedSecondUrl = productItem.dataset.originalSecondImageSrc || '';
          normalizedSecondSrcset = productItem.dataset.originalSecondImageSrcset || '';
        }
        
        if (normalizedSecondUrl) {
          if (normalizedSecondSrcset) {
            currentSecondImage.srcset = normalizedSecondSrcset;
          } else {
            currentSecondImage.removeAttribute('srcset');
          }
          
          currentSecondImage.decoding = 'sync';
          currentSecondImage.loading = 'eager';
          currentSecondImage.src = normalizedSecondUrl;
          
          productItem.dataset.currentSecondImageUrl = normalizedSecondUrl;
        }
      }
    }
    
    function getProductImage(productItem) {
      const allImages = productItem.querySelectorAll('.product-item__image img');
      for (let img of allImages) {
        const wrapper = img.closest('.product-item__image');
        if (wrapper && !wrapper.classList.contains('product-item__image--two') && !wrapper.classList.contains('LOP-second-image-wrapper')) {
          return img;
        }
      }
      
      return productItem.querySelector('.product-item__image-link img') ||
             productItem.querySelector('.product-item__media img');
    }
    
    function getOrCreateSecondImage(productItem, mainImage) {
      let secondImageWrapper = productItem.querySelector('.product-item__image--two');
      if (secondImageWrapper) {
        return secondImageWrapper.querySelector('img');
      }
      
      secondImageWrapper = productItem.querySelector('.LOP-second-image-wrapper');
      if (secondImageWrapper) {
        return secondImageWrapper.querySelector('img');
      }
      
      if (!mainImage) return null;
      
      const mainImageWrapper = mainImage.closest('.product-item__image');
      if (!mainImageWrapper) return null;
      
      const imageLink = mainImageWrapper.closest('.product-item__image-link');
      if (!imageLink) return null;
      
      const newWrapper = document.createElement('div');
      newWrapper.className = 'product-item__image product-item__image--two LOP-second-image-wrapper image';
      newWrapper.style.cssText = `
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        height: 100%;
        z-index: 2;
        opacity: 0;
        transform: scale(1.05);
        background-color: var(--color-background, #fff);
        transition: opacity 0.3s ease, transform 0.3s ease;
        pointer-events: none;
      `;
      
      const newImg = document.createElement('img');
      newImg.className = 'image__img b4b-image';
      newImg.alt = mainImage.alt || '';
      newImg.loading = 'eager';
      newImg.decoding = 'sync';
      newImg.style.cssText = `
        width: 100%;
        height: 100%;
        object-fit: cover;
      `;
      
      newWrapper.appendChild(newImg);
      imageLink.appendChild(newWrapper);
      
      mainImageWrapper.classList.add('product-item__image--one');
      
      const mediaContainer = productItem.querySelector('.product-item__media');
      if (mediaContainer) {
        mediaContainer.classList.add('product-item__media--multiple-images');
      }
      
      return newImg;
    }
    
    function showSecondImage(wrapper) {
      if (wrapper) {
        wrapper.style.opacity = '1';
        wrapper.style.transform = 'scale(1)';
      }
    }
    
    function hideSecondImage(wrapper) {
      if (wrapper) {
        wrapper.style.opacity = '0';
        wrapper.style.transform = 'scale(1.05)';
      }
    }
    
    // LOP: Format price in cents to currency format
    function formatMoney(cents) {
      if (typeof cents === 'string') {
        cents = parseInt(cents, 10);
      }
      if (isNaN(cents)) return '';
      
      // Try to use Shopify's money format if available
      if (window.Shopify && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(cents);
      }
      
      // Fallback: format as currency (assumes USD/EUR style)
      const amount = (cents / 100).toFixed(2);
      
      // Try to get currency symbol from existing price on page
      const existingPrice = document.querySelector('.product-item__price .sale, .product-item__price span:not(.visually-hidden)');
      if (existingPrice) {
        const priceText = existingPrice.textContent.trim();
        // Match currency symbol with optional space (e.g., "$ 56.70" or "$56.70")
        const currencyMatch = priceText.match(/^([^\d]+)/);
        if (currencyMatch) {
          // Preserve the original format including space
          return currencyMatch[1] + amount;
        }
        const currencyMatchEnd = priceText.match(/([^\d\s]+)$/);
        if (currencyMatchEnd) {
          return amount + ' ' + currencyMatchEnd[1];
        }
      }
      
      // Default format with space: $ 56.70
      return '$ ' + amount;
    }
    
    // LOP: Update product price display
    function updateProductPrice(productItem, price, compareAtPrice) {
      const priceContainer = productItem.querySelector('.product-item__price');
      if (!priceContainer) return;
      
      // Store original price HTML if not already stored
      if (!productItem.dataset.originalPriceHtml) {
        productItem.dataset.originalPriceHtml = priceContainer.innerHTML;
      }
      
      const formattedPrice = formatMoney(price);
      const formattedCompareAtPrice = compareAtPrice ? formatMoney(compareAtPrice) : null;
      
      // Build the new price HTML
      let priceHtml = '';
      
      if (formattedCompareAtPrice) {
        // Has compare at price (on sale)
        priceHtml = '<span class="visually-hidden">Precio regular</span>';
        priceHtml += '<s class="t-subdued">' + formattedCompareAtPrice + '</s> ';
        priceHtml += '<span class="sale">' + formattedPrice + '</span>';
      } else {
        // Regular price
        priceHtml = '<span>' + formattedPrice + '</span>';
      }
      
      priceContainer.innerHTML = priceHtml;
    }
    
    function normalizeImageUrl(url) {
      if (!url) return url;
      url = url.replace(/&amp;/g, '&');
      if (url.startsWith('//')) {
        return window.location.protocol + url;
      }
      return url;
    }
    
    function preloadImage(url, srcset) {
      if (!url || preloadedImages.has(url)) return;
      
      const img = new Image();
      if (srcset) {
        img.srcset = srcset;
      }
      img.src = url;
      preloadedImages.set(url, img);
    }
    
    function initSingleProductItem(productItem) {
      if (initializedHover.has(productItem)) return;
      
      // Gallery cards (section edit with switch ON): only preload swatches, skip LOP hover
      if (productItem.querySelector('.product-item__media--gallery')) {
        initializedHover.add(productItem);
        productItem.dataset.lopSwatchInit = 'true';
        var galSwatches = productItem.querySelectorAll('.product-swatches-options__item--swatch[data-variant-image-url]');
        if (!galSwatches.length) galSwatches = productItem.querySelectorAll('.product-swatches-options__item--swatch');
        galSwatches.forEach(function(swatch) {
          var variantImageUrl = swatch.getAttribute('data-variant-image-url');
          var variantSecondImageUrl = swatch.getAttribute('data-variant-second-image-url');
          if (variantImageUrl) preloadImage(normalizeImageUrl(variantImageUrl));
          if (variantSecondImageUrl) preloadImage(normalizeImageUrl(variantSecondImageUrl));
          swatch.style.cursor = 'pointer';
        });
        return;
      }
      
      const productImage = getProductImage(productItem);
      if (!productImage) {
        initializedHover.add(productItem);
        return;
      }
      
      if (!productItem.dataset.originalImageSrc) {
        productItem.dataset.originalImageSrc = productImage.src;
        productItem.dataset.originalImageSrcset = productImage.srcset || '';
        productItem.dataset.originalImageSizes = productImage.sizes || '';
        productItem.dataset.originalImageAlt = productImage.alt || '';
      }
      
      let swatches = productItem.querySelectorAll('.product-swatches-options__item--swatch[data-variant-image-url]');
      if (!swatches.length) {
        swatches = productItem.querySelectorAll('.product-swatches-options__item--swatch');
      }
      
      let existingSecondImageWrapper = productItem.querySelector('.product-item__image--two:not(.LOP-second-image-wrapper)');
      let existingSecondImage = existingSecondImageWrapper ? existingSecondImageWrapper.querySelector('img') : null;
      
      if (existingSecondImage && !productItem.dataset.originalSecondImageSrc) {
        productItem.dataset.originalSecondImageSrc = existingSecondImage.src || '';
        productItem.dataset.originalSecondImageSrcset = existingSecondImage.srcset || '';
      }
      
      let secondImage = null;
      let secondImageWrapper = null;
      
      if (existingSecondImage) {
        secondImage = existingSecondImage;
        secondImageWrapper = existingSecondImageWrapper;
        productItem.dataset.currentSecondImageUrl = existingSecondImage.src;
      } else if (swatches.length > 0) {
        secondImage = getOrCreateSecondImage(productItem, productImage);
        if (secondImage) {
          secondImageWrapper = secondImage.closest('.product-item__image--two, .LOP-second-image-wrapper');
          
          const currentMainImageSrc = productImage.src;
          const getFilename = (url) => {
            if (!url) return '';
            return url.split('?')[0].split('/').pop();
          };
          const currentFilename = getFilename(currentMainImageSrc);
          
          for (let swatch of swatches) {
            const swatchImageUrl = swatch.getAttribute('data-variant-image-url');
            if (swatchImageUrl && getFilename(swatchImageUrl) === currentFilename) {
              const secondUrl = swatch.getAttribute('data-variant-second-image-url');
              if (secondUrl) {
                secondImage.src = normalizeImageUrl(secondUrl);
                productItem.dataset.currentSecondImageUrl = secondImage.src;
                productItem.dataset.originalSecondImageSrc = secondImage.src;
              }
              break;
            }
          }
        }
      }
      
      const imageLink = productItem.querySelector('.product-item__image-link');
      
      if (secondImageWrapper && imageLink && !imageLink.dataset.lopHoverListeners) {
        imageLink.addEventListener('mouseenter', function() {
          const currentSecondWrapper = productItem.querySelector('.product-item__image--two');
          const currentSecondImg = currentSecondWrapper ? currentSecondWrapper.querySelector('img') : null;
          if (currentSecondImg && currentSecondImg.src && currentSecondImg.src !== '') {
            showSecondImage(currentSecondWrapper);
          }
        });
        
        imageLink.addEventListener('mouseleave', function() {
          const currentSecondWrapper = productItem.querySelector('.product-item__image--two');
          if (currentSecondWrapper) {
            hideSecondImage(currentSecondWrapper);
          }
        });
        
        imageLink.dataset.lopHoverListeners = 'true';
        productItem.dataset.lopHoverInit = 'true';
      } else if (!secondImageWrapper) {
        productItem.dataset.lopHoverInit = 'true';
      }
      
      swatches.forEach((swatch) => {
        const variantImageUrl = swatch.getAttribute('data-variant-image-url');
        const variantSecondImageUrl = swatch.getAttribute('data-variant-second-image-url');
        
        if (variantImageUrl) {
          preloadImage(normalizeImageUrl(variantImageUrl));
        }
        if (variantSecondImageUrl) {
          preloadImage(normalizeImageUrl(variantSecondImageUrl));
        }
        
        swatch.style.cursor = 'pointer';
      });
      
      initializedHover.add(productItem);
      productItem.dataset.lopSwatchInit = 'true';
    }
    
    function initProductItemSwatches() {
      const productItems = document.querySelectorAll('.product-item');
      if (!productItems.length) return;
      
      productItems.forEach((productItem) => {
        initSingleProductItem(productItem);
      });
    }
    
    function tryInit() {
      try {
        initProductItemSwatches();
      } catch (error) {
        console.error('LOP Swatches: Error initializing:', error);
      }
    }
    
    const debouncedInit = debounce(tryInit, 150);
    
    let intersectionObserver = null;
    
    function setupIntersectionObserver() {
      if (!window.IntersectionObserver) return;
      
      intersectionObserver = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            const productItem = entry.target;
            try {
              initSingleProductItem(productItem);
            } catch (error) {
              console.error('LOP Swatches: Error initializing product:', error);
            }
            intersectionObserver.unobserve(productItem);
          }
        });
      }, {
        rootMargin: '200px 0px',
        threshold: 0
      });
      
      observeNewProducts();
    }
    
    function observeNewProducts() {
      if (!intersectionObserver) return;
      
      const productItems = document.querySelectorAll('.product-item');
      productItems.forEach(function(productItem) {
        if (!initializedHover.has(productItem) && productItem.dataset.lopHoverInit !== 'true') {
          intersectionObserver.observe(productItem);
        }
      });
    }
    
    // Initialize on DOM ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        setupGlobalSwatchClickHandler();
        setupGlobalHoverHandler();
        tryInit();
        setupIntersectionObserver();
      });
    } else {
      setTimeout(function() {
        setupGlobalSwatchClickHandler();
        setupGlobalHoverHandler();
        tryInit();
        setupIntersectionObserver();
      }, 100);
    }
    
    // Re-initialize when Ajaxinate loads more products
    if (typeof window !== 'undefined' && window.MutationObserver) {
      const observer = new MutationObserver(function(mutations) {
        let hasNewProducts = false;
        
        mutations.forEach(function(mutation) {
          if (mutation.addedNodes.length) {
            mutation.addedNodes.forEach(function(node) {
              if (node.nodeType === 1) {
                if (node.classList && node.classList.contains('product-item')) {
                  hasNewProducts = true;
                } else if (node.querySelectorAll) {
                  const products = node.querySelectorAll('.product-item');
                  if (products.length > 0) {
                    hasNewProducts = true;
                  }
                }
              }
            });
          }
        });
        
        if (hasNewProducts) {
          debouncedInit();
          setTimeout(observeNewProducts, 200);
        }
      });
      
      function startObserving() {
        if (document.body) {
          observer.observe(document.body, {
            childList: true,
            subtree: true
          });
        } else {
          setTimeout(startObserving, 50);
        }
      }
      startObserving();
    }
    
    document.addEventListener('shopify:section:load', debouncedInit);
    document.addEventListener('shopify:section:reorder', debouncedInit);
    
    // Allow sections (e.g. featured-collection-slider-edit) to request init of product items in a container
    document.addEventListener('lop:init-product-items', function(e) {
      var container = e.detail && e.detail.container;
      if (!container || !container.querySelectorAll) return;
      var productItems = container.querySelectorAll('.product-item');
      productItems.forEach(function(productItem) {
        try {
          initSingleProductItem(productItem);
        } catch (err) {
          console.error('LOP Swatches: Error initializing product item:', err);
        }
      });
    });
    
    // Periodic check for new products
    let lastProductCount = 0;
    setInterval(function() {
      const currentCount = document.querySelectorAll('.product-item').length;
      if (currentCount !== lastProductCount) {
        lastProductCount = currentCount;
        debouncedInit();
        observeNewProducts();
      }
    }, 1000);
    
    // Force re-initialization on scroll
    const debouncedScrollInit = debounce(function() {
      const productItems = document.querySelectorAll('.product-item');
      productItems.forEach(function(productItem) {
        if (!initializedHover.has(productItem)) {
          initSingleProductItem(productItem);
        }
      });
    }, 500);
    
    window.addEventListener('scroll', debouncedScrollInit, { passive: true });
    
  })();
